/**
 * Package Publish Service
 *
 * Handles community publishing of custom skill packages.
 * Manages publishing, unpublishing, and community package discovery.
 */

import { generateClient } from 'aws-amplify/data';
import { getCurrentUser } from 'aws-amplify/auth';
import type { Schema } from "@backend-schema";
import type { CommunityPackage, PackageVisibility } from '../types/packageSharing';
import { copyPackage, hasExistingCopy } from './packageCopyService';

const client = generateClient<Schema>();

/**
 * Publish a custom skill package to the community.
 *
 * - Creates a new CommunityPackage record with moderationStatus 'pending_ai_review'.
 * - For re-publish of a rejected or pending_human_review package: resets moderationStatus
 *   to 'pending_ai_review' to trigger the AI moderation stream again.
 * - For re-publish of an approved package: restores visibility without re-moderating.
 * - Throws if the source package is not structurally complete (< 1 category or < 1 skill).
 */
export async function publishPackage(
  packageId: string,
  publisherDisplayName: string,
  visibility: PackageVisibility = 'public',
): Promise<void> {
  const user = await getCurrentUser();

  // Get the package details
  const pkgResult = await client.models.CustomSkillPackages.get({ id: packageId });
  if (!pkgResult.data) throw new Error('Package not found');

  const pkg = pkgResult.data;

  // Get category names and skill count for preview
  const categories = await client.models.CustomSkillCategories.list({
    filter: { customPackageId: { eq: packageId } },
    limit: 50,
  });

  // Structural completeness check (Req 1.3): must have ≥ 1 category and ≥ 1 skill
  if (categories.data.length === 0) {
    throw new Error('Package must contain at least 1 category and 1 skill before publishing');
  }

  const categoryNames = categories.data.map(c => c.name || '').filter(Boolean);
  let skillCount = 0;
  for (const cat of categories.data) {
    const skills = await client.models.CustomSkills.list({
      filter: { customCategoryId: { eq: cat.id ?? undefined } },
      limit: 200,
    });
    skillCount += skills.data.length;
  }

  if (skillCount === 0) {
    throw new Error('Package must contain at least 1 category and 1 skill before publishing');
  }

  // Check for an existing CommunityPackage record (re-publish scenario)
  const existingResult = await client.models.CommunityPackage.list({
    filter: { packageId: { eq: packageId } },
    limit: 1,
  });

  if (existingResult.data.length > 0) {
    const existing = existingResult.data[0];
    const currentStatus = existing.moderationStatus as string | null | undefined;

    if (currentStatus === 'rejected' || currentStatus === 'pending_human_review') {
      // Re-publish after rejection: reset to pending_ai_review to trigger moderation
      await client.models.CommunityPackage.update({
        id: existing.id || '',
        isActive: true,
        visibility,
        moderationStatus: 'pending_ai_review',
        categoryNames: JSON.stringify(categoryNames),
        skillCount,
      });
    } else {
      // Re-publish of an approved (or pending_ai_review) package: restore visibility
      // without resetting moderation status (Req 11.5)
      await client.models.CommunityPackage.update({
        id: existing.id || '',
        isActive: true,
        visibility,
        categoryNames: JSON.stringify(categoryNames),
        skillCount,
      });
    }
  } else {
    // First-time publish: create record with moderationStatus 'pending_ai_review'
    await client.models.CommunityPackage.create({
      packageId,
      packageName: pkg.name || 'Unnamed Package',
      packageDescription: pkg.description || undefined,
      packageVersion: pkg.version || 1,
      publisherDisplayName,
      publisherUserId: user.username,
      publishedAt: new Date().toISOString(),
      subscriberCount: 0,
      isActive: true,
      moderationStatus: 'pending_ai_review',
      visibility,
      categoryNames: JSON.stringify(categoryNames),
      skillCount,
      owner: user.username,
    });
  }

  // Update the source package published timestamp
  await client.models.CustomSkillPackages.update({
    id: packageId,
    publishedAt: new Date().toISOString(),
  });
}

/**
 * Unpublish a community package.
 * Deactivates the CommunityPackage record but retains existing user copies.
 */
export async function unpublishPackage(packageId: string): Promise<void> {
  // Find the CommunityPackage record
  const communityResult = await client.models.CommunityPackage.list({
    filter: {
      packageId: { eq: packageId },
      isActive: { eq: true },
    },
    limit: 1,
  });

  if (communityResult.data.length === 0) {
    throw new Error('Package is not published');
  }

  // Deactivate
  await client.models.CommunityPackage.update({
    id: communityResult.data[0].id || '',
    isActive: false,
  });

  // Update source package visibility (back to private, unless also shared)
  await client.models.CustomSkillPackages.update({
    id: packageId,
    visibility: 'private',
    publishedAt: undefined,
  });
}

/**
 * Add a community package to the current user's profile.
 * Creates a structural copy (no ratings) and increments subscriber count.
 */
export async function addCommunityPackage(
  communityPackageId: string,
): Promise<string> {
  // Get the CommunityPackage record
  const communityResult = await client.models.CommunityPackage.get({ id: communityPackageId });
  if (!communityResult.data) throw new Error('Community package not found');
  if (!communityResult.data.isActive) throw new Error('This package has been removed by the publisher');

  const community = communityResult.data;

  // Check for existing copy (prevent duplicates)
  const alreadyHas = await hasExistingCopy(community.packageId || '');
  if (alreadyHas) {
    throw new Error('You have already added this package');
  }

  // Copy the package
  const copyResult = await copyPackage({
    sourcePackageId: community.packageId || '',
    sourceVersion: community.packageVersion || 1,
    sharedByName: community.publisherDisplayName || '',
  });

  // Increment subscriber count
  await client.models.CommunityPackage.update({
    id: communityPackageId,
    subscriberCount: (community.subscriberCount || 0) + 1,
  });

  return copyResult.newPackageId;
}

/**
 * Get all active, approved community packages, optionally filtered by publisher.
 * Only returns packages with isActive = true AND moderationStatus = 'approved' (Req 5.1, 11.5).
 */
export async function getCommunityPackages(
  filter?: { publisherDisplayName?: string },
): Promise<CommunityPackage[]> {
  const queryFilter: Record<string, unknown> = {
    isActive: { eq: true },
    moderationStatus: { eq: 'approved' },
  };

  if (filter?.publisherDisplayName) {
    queryFilter.publisherDisplayName = { eq: filter.publisherDisplayName };
  }

  const result = await client.models.CommunityPackage.list({
    filter: queryFilter,
    limit: 100,
  });

  return result.data.map(cp => ({
    id: cp.id || '',
    packageId: cp.packageId || '',
    packageName: cp.packageName || '',
    packageDescription: cp.packageDescription || null,
    packageVersion: cp.packageVersion || 1,
    publisherDisplayName: cp.publisherDisplayName || '',
    publisherUserId: cp.publisherUserId || '',
    publishedAt: cp.publishedAt || '',
    updatedAt: cp.updatedAt || null,
    subscriberCount: cp.subscriberCount || 0,
    isActive: cp.isActive ?? true,
    categoryNames: cp.categoryNames || null,
    skillCount: cp.skillCount || null,
    owner: cp.owner || '',
    moderationStatus: (cp.moderationStatus as CommunityPackage['moderationStatus']) || 'pending_ai_review',
    visibility: (cp.visibility as CommunityPackage['visibility']) || 'public',
  }));
}
